import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { authService } from "../../services/auth.service";
import { useAuth } from "../../context/AuthContext";
import { tagOf } from "../../utils/api-error";
import { registerSchema } from "./validations/auth.schema";
import Mascot from "../../components/brand/Mascot";
import DoodleUnderline from "../../components/brand/DoodleUnderline";

// Tags this endpoint documents (docs/web-api-spec.md §POST /auth/register).
// Anything else is a server-side surprise and gets the one generic message.
const KNOWN_TAGS = ["email_taken", "weak_password", "rate_limited"];

const Register = () => {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const from = (useLocation().state as { from?: string } | null)?.from;
  const [failureTag, setFailureTag] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(registerSchema) });

  const fieldError = (message?: string) =>
    message ? (
      <p className="mt-1 text-sm text-red-600">{t(message, { min: 8 })}</p>
    ) : null;

  const onSubmit = handleSubmit(async (values) => {
    setFailureTag(null);
    try {
      const { tempToken } = await authService.register(values);
      // Router state, never the URL: a tempToken in the query string lands in
      // browser history, the Referer header, and every analytics script.
      navigate("/verify-otp", {
        state: { tempToken, email: values.email, from },
      });
    } catch (err) {
      setFailureTag(tagOf(err) ?? "generic");
    }
  });

  if (!loading && isAuthenticated) return <Navigate to={from ?? "/account"} replace />;

  return (
    <div className="mx-auto mt-8 grid max-w-4xl overflow-hidden rounded-xl3 border border-hairline shadow-lift sm:grid-cols-[1.05fr_.95fr]">
      <aside
        aria-hidden="true"
        className="hidden bg-hero p-12 sm:flex sm:flex-col"
      >
        <span className="grid h-10 w-10 -rotate-3 place-items-center rounded-xl bg-apricot text-lg font-extrabold text-white">
          學
        </span>
        <p className="mt-8 font-display text-3xl font-extrabold text-ink">
          {t("marketing:home.hero.title")}
        </p>
        <Mascot className="mt-6 w-32" />
        <DoodleUnderline className="h-3 w-40 text-teal" />
      </aside>

      <div className="bg-surface p-10">
        <h1 className="mb-6 font-display text-2xl font-extrabold">
          {t("register.title")}
        </h1>

        {failureTag && (
          <div
            role="alert"
            className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            <p>
              {KNOWN_TAGS.includes(failureTag)
                ? t(`errors.${failureTag}`)
                : t("common:errors.generic")}
            </p>
            {failureTag === "email_taken" && (
              <Link to="/login" className="font-bold text-teal-dark underline">
                {t("register.haveAccount")}
              </Link>
            )}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div>
            <label
              htmlFor="firstName"
              className="mb-1 block text-sm font-bold"
            >
              {t("fields.firstName")}
            </label>
            <input
              id="firstName"
              className="field-input"
              {...register("firstName")}
            />
            {fieldError(errors.firstName?.message)}
          </div>

          <div>
            <label htmlFor="lastName" className="mb-1 block text-sm font-bold">
              {t("fields.lastName")}
            </label>
            <input
              id="lastName"
              className="field-input"
              {...register("lastName")}
            />
            {fieldError(errors.lastName?.message)}
          </div>

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-bold">
              {t("fields.email")}
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className="field-input"
              {...register("email")}
            />
            {fieldError(errors.email?.message)}
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-bold"
            >
              {t("fields.password")}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className="field-input"
              {...register("password")}
            />
            {fieldError(errors.password?.message)}
          </div>

          <button type="submit" disabled={isSubmitting} className="btn w-full">
            {t("register.submit")}
          </button>
        </form>

        <p className="mt-6 text-sm">
          {t("register.haveAccount")}{" "}
          <Link to="/login" className="font-bold text-teal-dark underline">
            {t("login.submit")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
